import { Button, buttonVariants } from '../components/ui/button';
import { FaArrowLeft, FaArrowRight, FaCheck, FaLightbulb, FaPause } from 'react-icons/fa';
import React, { useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { Chess } from 'chess.js';
import catalog from './catalog.json';
import OpeningBoard, { linePosition } from './OpeningBoard';
import { OpeningSignIn } from './OpeningLayout';
import { openingRequest } from './api';
import useOpeningSet from './useOpeningSet';
import { formatTime } from './OpeningBriefing';

export default function OpeningTraining() {
  const { setId, lineId } = useParams();
  const [query] = useSearchParams();
  const { auth, data, error, reload } = useOpeningSet(setId);
  if (lineId) {
    const line = catalog.lines.find(item => item.id === lineId);
    if (!line) return <p>Line not found. <Link to="/openings">Back to openings</Link></p>;
    const side = query.get('side') === 'black' ? 'black' : 'white';
    const sampleSet = {
      setId: 'sample', name: line.name, side, lineIds: [lineId], wrongLineIds: [],
      cycleNumber: 1, revision: 0, catalogVersion: catalog.version,
      progress: { mode: 'full', lineIds: [lineId], lineIndex: 0, moveIndex: 0, timeSpent: 0, solvedCorrectly: 0, failed: false, assisted: false },
    };
    return <Session key={`${lineId}-${side}`} initialSet={sampleSet} auth={auth} sample />;
  }
  if (!auth.isAuthenticated) return <OpeningSignIn />;
  if (error) return <p role="alert" className="opening-error">{error} <Button variant="neutral" size="action" className="m-0 border-solid" onClick={reload}>Retry</Button></p>;
  if (!data) return <p role="status">Loading your cycle...</p>;
  if (!data.set.progress) return <p>This cycle is complete. <Link to={`/openings/sets/${setId}`}>Return to progress</Link></p>;
  return <Session key={data.set.setId} initialSet={data.set} auth={auth} />;
}

function Session({ initialSet, auth, sample = false }) {
  const [set, setSet] = useState(initialSet);
  const [progress, setProgress] = useState(initialSet.progress);
  const [busy, setBusy] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [message, setMessage] = useState('Play the displayed variation.');
  const [hint, setHint] = useState(null);
  const [sampleDone, setSampleDone] = useState(false);
  const saving = useRef(false);
  const lastSave = useRef({});
  const navigate = useNavigate();
  const line = catalog.lines.find(item => item.id === progress.lineIds[progress.lineIndex]);
  const game = line ? linePosition(line, progress.moveIndex) : null;
  const atEnd = Boolean(line && progress.moveIndex === line.moves.length && game.fen() === line.targetFen);
  const opponentTurn = game && (game.turn() === 'w' ? 'white' : 'black') !== set.side;
  const canMove = Boolean(line && !atEnd && !opponentTurn && !busy && !saveError && !sampleDone);

  useEffect(() => {
    if (!line || atEnd || busy || saveError || sampleDone) return;
    const timer = setInterval(() => setProgress(current => ({ ...current, timeSpent: current.timeSpent + 1 })), 1000);
    return () => clearInterval(timer);
  }, [line, atEnd, busy, saveError, sampleDone]);

  useEffect(() => {
    if (!opponentTurn || atEnd || busy || saveError || sampleDone) return;
    const timer = setTimeout(() => setProgress(current => ({ ...current, moveIndex: current.moveIndex + 1 })), 550);
    return () => clearTimeout(timer);
  }, [opponentTurn, atEnd, busy, saveError, sampleDone, progress.moveIndex]);

  useEffect(() => { setHint(null); }, [progress.lineIndex, progress.moveIndex]);

  const save = async ({ advance = false, exit = false } = {}) => {
    if (saving.current) return;
    if (sample) {
      if (exit) navigate('/openings');
      else if (advance) setSampleDone(true);
      return;
    }
    if (!advance && ['moveIndex', 'timeSpent', 'failed', 'assisted'].every(key => progress[key] === set.progress[key])) {
      if (exit) navigate(`/openings/sets/${set.setId}`);
      return;
    }
    saving.current = true; setBusy(true); setSaveError(null);
    lastSave.current = { advance, exit };
    try {
      const result = await openingRequest(`/${set.setId}`, auth, 'PATCH', {
        revision: set.revision, moveIndex: progress.moveIndex, timeSpent: progress.timeSpent,
        failed: progress.failed, assisted: progress.assisted, advance,
      });
      setSet(result.set);
      if (result.set.progress) setProgress(result.set.progress);
      if (exit || !result.set.progress) navigate(`/openings/sets/${set.setId}`, { state: { completedCycle: result.cycle?.cycleNumber } });
      else if (advance) setMessage('Play the displayed variation.');
    } catch (error) { setSaveError(error); }
    finally { saving.current = false; setBusy(false); }
  };

  useEffect(() => {
    const warn = event => { event.preventDefault(); event.returnValue = ''; };
    if (!sample && !sampleDone) window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [sample, sampleDone]);

  const move = (from, to, promotion = 'q') => {
    if (!canMove) return;
    const attempt = new Chess(game.fen());
    let played;
    try { played = attempt.move({ from, to, promotion }); }
    catch { setMessage('That move is not legal in this position. Try another move.'); return; }
    const uci = played.from + played.to + (played.promotion || '');
    if (uci !== line.moves[progress.moveIndex]) {
      setProgress(current => ({ ...current, failed: true }));
      setMessage('That is not the move in this training line. Try again.');
    } else {
      setProgress(current => ({ ...current, moveIndex: current.moveIndex + 1 }));
      setMessage('Correct move.');
    }
  };
  const help = () => {
    const expected = line.moves[progress.moveIndex];
    setProgress(current => ({ ...current, assisted: true }));
    setHint(expected.slice(0, 2));
    setMessage('Move the highlighted piece. This attempt will be marked as assisted.');
  };

  if (!line || set.catalogVersion !== catalog.version) return <p role="alert">This line is unavailable. <Link to="/openings">Return to opening sets</Link></p>;
  if (sampleDone) return <section className="opening-panel">
    <h1>Sample line complete</h1><p>{progress.failed || progress.assisted ? 'You reached the target position. Try again without mistakes or help.' : 'You recalled the complete variation without help.'}</p>
    <div className="opening-actions"><Button variant="neutral" size="action" className="m-0 border-solid" onClick={() => { setProgress(initialSet.progress); setSampleDone(false); setMessage('Play the displayed variation.'); }}>Try again</Button><Link className={buttonVariants({ variant: 'brand', size: 'action', className: 'm-0 border-solid' })} to="/openings">Build a training set</Link></div>
  </section>;
  return <>
    <Helmet><meta name="robots" content="noindex,follow" /></Helmet>
    <div className="opening-eyebrow">{sample ? 'Sample line' : `Cycle ${set.cycleNumber} · ${progress.mode === 'mistakes' ? 'Mistakes only' : 'All lines'}`}</div>
    <h1>{line.name}</h1><p>Playing {set.side === 'white' ? 'White' : 'Black'} · Line {progress.lineIndex + 1} of {progress.lineIds.length}</p>
    {saveError && <div role="alert" className="opening-error">{saveError.message}<div className="opening-actions">{saveError.status === 409 ? <Button variant="neutral" size="action" className="m-0 border-solid" onClick={() => window.location.reload()}>Reload saved progress</Button> : <Button variant="brand" size="action" className="m-0 border-solid" onClick={() => save(lastSave.current)}>Retry save</Button>}</div></div>}
    <div className="opening-grid">
      <section className="opening-session-board">
        <OpeningBoard game={game} side={set.side} disabled={!canMove} hint={hint} onMove={move} />
        <div role="status" aria-live="polite" className="opening-feedback">{busy ? 'Saving your progress...' : atEnd ? 'Line complete. Target position reached.' : opponentTurn ? 'Opponent is replying...' : message}</div>
        {atEnd ? <Button variant="brand" size="action" className="m-0 w-12 p-0" aria-label={progress.lineIndex === progress.lineIds.length - 1 ? 'Finish cycle' : 'Next line'} title={progress.lineIndex === progress.lineIds.length - 1 ? 'Finish cycle' : 'Next line'} disabled={busy || Boolean(saveError)} onClick={() => save({ advance: true })}>{progress.lineIndex === progress.lineIds.length - 1 ? <FaCheck aria-hidden="true" /> : <FaArrowRight aria-hidden="true" />}</Button> : <div className="opening-actions"><Button variant="neutral" size="action" className="m-0 w-12 border-solid p-0" aria-label="Hint" title="Hint" disabled={!canMove} onClick={help}><FaLightbulb aria-hidden="true" /></Button></div>}
      </section>
      <section className="opening-panel">
        <h2>{set.name}</h2>
        <div className="opening-stats"><span><strong>{formatTime(progress.timeSpent)}</strong>Active time</span><span><strong>{progress.moveIndex} / {line.moves.length}</strong>Half-moves played</span></div>
        <p>{progress.failed ? 'This line will stay in your mistakes for another cycle.' : progress.assisted ? 'Assisted attempt: this line will not count toward unaided accuracy.' : 'Follow the selected variation through to its target position.'}</p>
        <div className="opening-actions"><Button variant="brand" size="action" className="m-0 w-12 p-0" aria-label={sample ? 'Back to openings' : 'Pause and save'} title={sample ? 'Back to openings' : 'Pause and save'} disabled={busy} onClick={() => save({ exit: true })}>{sample ? <FaArrowLeft aria-hidden="true" /> : <FaPause aria-hidden="true" />}</Button></div>
        <p className="opening-muted">{sample ? 'This sample is not saved.' : 'Progress saves on Next line, Finish cycle, or Pause & Save. Use one of these before leaving.'}</p>
      </section>
    </div>
  </>;
}
